import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sparkle } from '@phosphor-icons/react';
import MilestoneCelebration from '@/components/MilestoneCelebration';
import { useMilestoneCheck } from '@/hooks/useMilestoneCheck';

const SavingsPlanner = () => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    target_amount: '',
    current_amount: '',
    target_date: ''
  });
  const { celebrations, triggerCheck, dismissAll } = useMilestoneCheck(false);

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const response = await api.get('/savings-goals');
      setGoals(response.data);
    } catch (error) {
      toast.error('Failed to load savings goals');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/savings-goals', {
        ...formData,
        target_amount: parseFloat(formData.target_amount),
        current_amount: parseFloat(formData.current_amount || 0),
        target_date: formData.target_date || null
      });
      toast.success('Savings goal added!');
      setFormData({ name: '', target_amount: '', current_amount: '', target_date: '' });
      fetchGoals();
      triggerCheck();
    } catch (error) {
      toast.error('Failed to add goal');
    }
  };

  return (
    <div className="space-y-8 fade-in">
      {celebrations.length > 0 && (
        <MilestoneCelebration
          celebrations={celebrations}
          onClose={dismissAll}
          onAcknowledgeAll={dismissAll}
        />
      )}
      {/* Educational Disclaimer */}
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Educational Tool:</strong> This savings planner helps you track goals. For personalized savings strategies, consult a qualified financial advisor.
          </p>
        </div>
      </div>

      <div>
        <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
          Savings Planner
        </h1>
        <p className="text-base text-[#4A5568]">
          Set and track your savings goals
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Add Goal Form */}
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="add-savings-goal-form">
          <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Add Savings Goal</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="goal_name">Goal Name</Label>
              <Input
                id="goal_name"
                data-testid="goal-name-input"
                placeholder="Emergency Fund"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="border-slate-300"
                required
              />
            </div>

            <div>
              <Label htmlFor="target_amount">Target Amount ($)</Label>
              <Input
                id="target_amount"
                type="number"
                step="0.01"
                data-testid="target-amount-input"
                value={formData.target_amount}
                onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                className="border-slate-300"
                required
              />
            </div>

            <div>
              <Label htmlFor="current_amount">Current Amount ($)</Label>
              <Input
                id="current_amount"
                type="number"
                step="0.01"
                data-testid="current-amount-input"
                value={formData.current_amount}
                onChange={(e) => setFormData({ ...formData, current_amount: e.target.value })}
                className="border-slate-300"
              />
            </div>

            <div>
              <Label htmlFor="target_date">Target Date (Optional)</Label>
              <Input
                id="target_date"
                type="date"
                data-testid="target-date-input"
                value={formData.target_date}
                onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                className="border-slate-300"
              />
            </div>

            <Button type="submit" data-testid="add-goal-button" className="w-full bg-[#0A1128] hover:bg-[#152145]">
              Add Goal
            </Button>
          </form>
        </div>

        {/* Goals List */}
        <div className="space-y-4">
          {goals.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-8 text-center">
              <p className="text-[#4A5568]">No savings goals yet. Add your first goal!</p>
            </div>
          ) : (
            goals.map(goal => {
              const progress = (goal.current_amount / goal.target_amount) * 100;
              return (
                <div key={goal.id} className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid={`goal-${goal.id}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="cabinet-grotesk text-lg font-medium text-[#0A1128]">{goal.name}</h3>
                      {goal.target_date && (
                        <p className="text-sm text-[#4A5568] mt-1">
                          Target: {new Date(goal.target_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-[#4A5568]">Progress</p>
                      <p className="text-xl font-semibold text-[#0A1128] ibm-plex">{progress.toFixed(0)}%</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4A5568]">Current</span>
                      <span className="font-medium text-[#1A202C] ibm-plex">${goal.current_amount.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                      <div 
                        className="bg-[#C5A059] h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(progress, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4A5568]">Target</span>
                      <span className="font-medium text-[#1A202C] ibm-plex">${goal.target_amount.toLocaleString()}</span>
                    </div>
                    <div className="pt-2 border-t border-slate-200">
                      <p className="text-sm text-[#4A5568]">
                        Remaining: <span className="font-medium text-[#1A202C] ibm-plex">${(goal.target_amount - goal.current_amount).toLocaleString()}</span>
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default SavingsPlanner;